import { computed, Directive, effect, inject, input, Signal, signal, viewChild, WritableSignal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TNil } from '@app/app.interface';
import { TDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { IManualDeliverableStatusConfig } from '@cta-analytics-details/cta-analytics-details-deliverable-card/manual-deliverable-status/manual-devlierable-status.interface';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { ICtaAnalyticsData } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-table.interface';
import { DeliverableFindingsService } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.service';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { FindingDeleteService } from '@cta-analytics-dialog/deliverable-analytics-content/finding-delete.service';
import { ManualFindingStatusStore } from '@cta-analytics-dialog/manual-finding-status.store';
import { EAnalyticsStatus, EMatchingType, EProcessStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { IBaseDeliverableAnalyticsApi } from '@interfaces/campaign-analytics-api.interface';
import { IFinding, IKeywordHolder } from '@interfaces/deliverable-analytics.interface';
import { IFindingVisualizer } from '@interfaces/finding-visualizer.interface';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';

@Directive({
  providers: [DeliverableFindingsStore],
  selector: 'app-text-post-analytics-base',
})
export abstract class TextPostAnalyticsBaseDirective<
  TableData extends { source?: string; text: string } | undefined,
  Deliverable extends IBaseDeliverableAnalyticsApi,
> {
  public readonly deliverableForm = input.required<FormGroup<TDeliverableForm>>();
  public readonly deliverableId = input.required<string | null>();
  public readonly executionAnalytics = input.required<ICtaAnalyticsData | null>();
  public readonly manualDeliverableStatusConfig = input.required<IManualDeliverableStatusConfig>();

  public readonly translate = inject(TranslateService);

  public abstract readonly kpiCardData: IKpiCard | null;
  public abstract readonly deliverableType: EDeliverableType;
  public readonly allKpiCards: WritableSignal<IKpiCard[]> = signal([]);

  public readonly findingsLoaded = signal<boolean>(false);

  public readonly executionAnalyticsWithFindings = computed(() => {
    const analytics = this.executionAnalytics();
    const findings = this.findingsStore.findings$();

    if (!analytics || !this.findingsLoaded()) {
      return analytics;
    }

    return this.findingsService.mergeFindingsIntoAnalytics(
      analytics,
      findings,
      this.deliverableId()!,
      this.deliverableType,
    );
  });

  protected readonly keywordCardList = signal<IKpiCard[]>([]);
  protected readonly status = signal<EAnalyticsStatus>(EAnalyticsStatus.processing);

  public readonly sort = viewChild<MatSort>(MatSort);
  public readonly displayedColumns: string[] = ['source', 'text'];
  public readonly dataSource: MatTableDataSource<TableData> = new MatTableDataSource();
  public readonly dataCount = signal<number | null>(null);

  public readonly EMatchingType = EMatchingType;

  protected abstract readonly currentDeliverable: Signal<Deliverable | TNil>;
  protected abstract readonly contentData: Signal<unknown | null> | null;

  public readonly manualFindingStatusStore = inject(ManualFindingStatusStore, { optional: true });

  public readonly getKeywordCount = (keyword: string): number | null => {
    const removedIds = this.manualFindingStatusStore?.allRemovedIds() ?? new Set<string>();
    const keywords =
      (this.currentDeliverable() as unknown as IKeywordHolder<IFinding> | null | undefined)?.keywords ?? [];

    return AnalyticsDetailsUtilitiesService.computeKeywordCount(keyword, keywords, removedIds);
  };

  protected readonly findingsStore = inject(DeliverableFindingsStore);
  protected readonly findingsService = inject(DeliverableFindingsService);
  private readonly findingDeleteService = inject(FindingDeleteService);

  public readonly canDelete = computed(
    () => this.executionAnalytics()?.campaignProcessStatus === EProcessStatus.processing,
  );

  protected keywords: string[] = [];
  protected keywordCounts: Record<string, number> = {};
  private lastFindingsRequestKey: string | null = null;

  constructor() {
    effect(() => {
      if (!this.manualDeliverableStatusConfig().analyticsVisible) {
        this.findingsLoaded.set(true);
        this.lastFindingsRequestKey = null;

        return;
      }

      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalytics();
      const socialContentId = executionAnalytics?.content?.socialContent?.uniqueExternalId;

      if (deliverableId && socialContentId) {
        const findingType = this.findingsService.mapDeliverableTypeToFindingType(this.deliverableType);

        if (findingType) {
          const findingsRequestKey = `${deliverableId}_${findingType}_${socialContentId}`;

          if (this.lastFindingsRequestKey === findingsRequestKey) {
            return;
          }

          this.lastFindingsRequestKey = findingsRequestKey;
          this.findingsLoaded.set(false);
          this.findingsStore.fetchFindings({
            campaignExportId: this.manualDeliverableStatusConfig().exportMode
              ? this.manualDeliverableStatusConfig().campaignExportId
              : null,
            deliverableId,
            findingType,
            socialContentId,
          });
        }
      }
    });

    effect(() => {
      const loading = this.findingsStore.loading$();

      if (!loading) {
        this.findingsLoaded.set(true);
      }
    });

    this.setAllKpiCardsEffect();
    this.initializeEffect();
    this.setKeywordsEffect();
  }

  protected setStatus(): void {
    this.status.set(
      this.currentDeliverable()?.statusManual ?? this.currentDeliverable()?.status ?? EAnalyticsStatus.processing,
    );
  }

  protected highlightKeywords(text: string, rowKeywords?: string[]): string {
    return AnalyticsDetailsUtilitiesService.highlightKeywords(rowKeywords ?? this.keywords, text);
  }

  protected setAllKpiCardsEffect(): void {
    effect(() => {
      this.allKpiCards.set([...(this.kpiCardData ? [this.kpiCardData] : []), ...this.keywordCardList()]);
    });
  }

  protected abstract setKeywordCounts(): void;
  protected abstract setKeywordCardList(): void;
  protected abstract setKeywordsEffect(): void;

  protected prepareTableData(): void {
    return;
  }

  public buildDeleteMenuItems(
    element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>,
  ): Array<{ label: string; findingId: string; keyword: string; keywordId: string }> {
    return this.findingDeleteService.buildDeleteMenuItems(element, this.manualFindingStatusStore);
  }

  public onDeleteFinding(
    findingId: string,
    element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>,
  ): void {
    if (!this.manualFindingStatusStore) return;

    const context = this.findingDeleteService.getDeleteContext(this.executionAnalytics(), this.deliverableId());

    if (!context) return;

    this.findingDeleteService.deleteFinding(findingId, element, this.manualFindingStatusStore, context);
  }

  public onDeleteAllFindings(element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>): void {
    if (!this.manualFindingStatusStore) return;

    const context = this.findingDeleteService.getDeleteContext(this.executionAnalytics(), this.deliverableId());

    if (!context) return;

    this.findingDeleteService.deleteAllFindings(element, this.manualFindingStatusStore, context);
  }

  public onRevertRow(element: Pick<IFindingVisualizer, 'id'>): void {
    this.findingDeleteService.revertRow(element, this.manualFindingStatusStore);
  }

  public hasRowPendingDeletions(element: Pick<IFindingVisualizer, 'id'>): boolean {
    return this.findingDeleteService.hasRowPendingDeletions(element, this.manualFindingStatusStore);
  }

  public hasRowActiveFinding(element: Pick<IFindingVisualizer, 'findings'>): boolean {
    return this.findingDeleteService.hasRowActiveFinding(element, this.manualFindingStatusStore);
  }

  public getActiveKeywords(element: Pick<IFindingVisualizer, 'findings' | 'keywordMappings'>): string[] {
    return this.findingDeleteService.getActiveKeywords(element, this.manualFindingStatusStore);
  }

  public isVisualizerActing(visualizerId: string): boolean {
    return this.findingDeleteService.isVisualizerActing(visualizerId, this.manualFindingStatusStore);
  }

  public isFindingInFlight(findingId: string): boolean {
    return this.findingDeleteService.isFindingInFlight(findingId, this.manualFindingStatusStore);
  }

  protected initializeEffect(): void {
    effect(() => {
      this.manualFindingStatusStore?.serverRemovedIds();

      if (this.executionAnalyticsWithFindings()) {
        this.prepareTableData();
        this.setKeywordCounts();
        this.setStatus();
        this.setKeywordCardList();
      }
    });
  }
}
